import { useEffect, useState } from 'react';

/**
 * "A phone, either way up" — the JS twin of the `compact` variant in
 * styles/responsive.css. Change one, change both (a test compares them).
 *
 * Narrower than `md` (the upright phone the app was designed for), OR shorter
 * than 32rem while narrower than `lg`: the same phone turned sideways, which is
 * 780–956px wide and so had been treated as a desktop — inline menu, settings
 * column and all — in about 330px of height. Tablets and counter monitors are
 * taller than 512px, so no desktop ever matches. responsive.css says why this
 * reads size and not `pointer: coarse`.
 *
 * `.98`, not the round pixel: `max-width: 767px` leaves a 767–768 gap that a
 * zoomed or fractional-DPR window can land in, matching neither side.
 */
export const COMPACT_QUERY =
  '(max-width: 767.98px), (max-height: 511.98px) and (max-width: 1023.98px)';

/** False wherever `matchMedia` is missing — i.e. the static-markup tests. */
export function isCompactViewport(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(COMPACT_QUERY).matches
    : false;
}

/**
 * Re-evaluates on every way a rotation can announce itself.
 *
 * The query's own `change` event is the real signal, but emulated viewports
 * (DevTools device mode, embedded panes) update `matches` without firing it —
 * observed in AppShell, not hypothetical — so `resize` and `orientationchange`
 * are listened to as well. Reading `matches` is cheap and the state setter
 * ignores an unchanged value, so the extra events cost nothing.
 */
export function subscribeCompactViewport(onChange: (compact: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const query = window.matchMedia(COMPACT_QUERY);
  const sync = () => onChange(query.matches);
  query.addEventListener('change', sync);
  window.addEventListener('resize', sync);
  window.addEventListener('orientationchange', sync);
  return () => {
    query.removeEventListener('change', sync);
    window.removeEventListener('resize', sync);
    window.removeEventListener('orientationchange', sync);
  };
}

/** Whether the viewport is compact right now, live across rotations. */
export function useCompactViewport(): boolean {
  const [compact, setCompact] = useState(isCompactViewport);
  useEffect(() => subscribeCompactViewport(setCompact), []);
  return compact;
}
